import React, { useEffect, useState } from "react";
import api, { fmtDate } from "@/lib/api";
import { PageHeader } from "@/components/layout/PageHeader";
import { Plus, Trash, X } from "@phosphor-icons/react";

export default function MovementOfItemsPage() {
  const [rows, setRows] = useState([]);
  const [assets, setAssets] = useState([]);
  const [locations, setLocations] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    asset_id: "", to_location_id: "",
    movement_date: new Date().toISOString().slice(0, 10),
    reason: "", performed_by: "",
  });

  const load = async () => {
    const [a, b, c] = await Promise.all([
      api.get("/alm/movements"), api.get("/alm/assets"), api.get("/alm/locations"),
    ]);
    setRows(a.data); setAssets(b.data); setLocations(c.data);
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    await api.post("/alm/movements", form);
    setOpen(false);
    setForm({ ...form, asset_id: "", to_location_id: "", reason: "", performed_by: "" });
    load();
  };

  const remove = async (id) => { if (window.confirm("Delete this movement?")) { await api.delete(`/alm/movements/${id}`); load(); } };

  return (
    <div>
      <PageHeader
        eyebrow="Asset Lifecycle"
        title="Movement of Items"
        subtitle="Record inter-location transfers — the asset's current location updates automatically."
        testid="alm-mov-title"
        actions={
          <button onClick={() => setOpen(true)} data-testid="alm-mov-new-btn" className="bg-[#0020C2] text-white px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-[#001AA3] flex items-center gap-2">
            <Plus size={14} weight="bold" /> New Movement
          </button>
        }
      />
      <div className="p-8">
        <div className="border border-gray-200 bg-white overflow-x-auto">
          <table className="w-full text-sm" data-testid="alm-mov-table">
            <thead className="bg-gray-50">
              <tr className="text-left">
                {["Asset", "From", "To", "Date", "Reason", "By", ""].map(h => (
                  <th key={h} className="px-4 py-3 text-[10px] tracking-[0.2em] uppercase font-bold text-gray-500 border-b border-gray-300">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-3 font-mono text-xs font-bold">{r.asset_tag}</td>
                  <td className="px-4 py-3 text-xs">{r.from_location_name || "—"}</td>
                  <td className="px-4 py-3 text-xs font-semibold">{r.to_location_name}</td>
                  <td className="px-4 py-3 font-mono text-xs">{fmtDate(r.movement_date)}</td>
                  <td className="px-4 py-3 text-xs">{r.reason}</td>
                  <td className="px-4 py-3 text-xs">{r.performed_by}</td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => remove(r.id)} data-testid={`alm-mov-del-${r.id}`} className="text-[#DC2626] hover:underline text-xs font-bold">
                      <Trash size={12} weight="bold" />
                    </button>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={7} className="px-4 py-12 text-center text-sm text-gray-500">No movements yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-stretch justify-end" onClick={() => setOpen(false)}>
          <form onClick={e => e.stopPropagation()} onSubmit={create} className="w-full max-w-md bg-white h-full overflow-y-auto" data-testid="alm-mov-form">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <div>
                <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-gray-500">Move</div>
                <h2 className="text-2xl font-black tracking-tight">New Movement</h2>
              </div>
              <button type="button" onClick={() => setOpen(false)}><X size={18} /></button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-gray-600 block mb-1.5">Asset *</label>
                <select required value={form.asset_id} onChange={e => setForm({ ...form, asset_id: e.target.value })} data-testid="alm-mov-asset" className="w-full border border-gray-300 px-3 py-2.5 text-sm">
                  <option value="">— Select Asset —</option>
                  {assets.map(a => <option key={a.id} value={a.id}>{a.asset_tag} — {a.product_code}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-gray-600 block mb-1.5">To Location *</label>
                <select required value={form.to_location_id} onChange={e => setForm({ ...form, to_location_id: e.target.value })} data-testid="alm-mov-tolocation" className="w-full border border-gray-300 px-3 py-2.5 text-sm">
                  <option value="">— Select —</option>
                  {locations.map(l => <option key={l.id} value={l.id}>{l.code} — {l.name}</option>)}
                </select>
              </div>
              {[
                ["movement_date", "Movement Date *", "date", true],
                ["reason", "Reason", "text", false],
                ["performed_by", "Performed By", "text", false],
              ].map(([k, label, type, required]) => (
                <div key={k}>
                  <label className="text-xs font-bold uppercase tracking-wider text-gray-600 block mb-1.5">{label}</label>
                  <input type={type} required={required} value={form[k]} onChange={e => setForm({ ...form, [k]: e.target.value })} data-testid={`alm-mov-${k}`} className="w-full border border-gray-300 px-3 py-2.5 text-sm" />
                </div>
              ))}
            </div>
            <div className="p-6 border-t border-gray-200">
              <button type="submit" data-testid="alm-mov-submit" className="w-full bg-[#0020C2] text-white py-3 text-sm font-bold uppercase tracking-wider hover:bg-[#001AA3]">Post Movement</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
